import * as React from 'react';
import { observableValue, autorun } from '@vscode/observables';
import { view, viewWithModel, ViewModel, prop } from '@vscode/observables-react';
import type { DaemonModel } from '../daemon/index.js';
import type { ScreenshotComparisonItem } from './ExplorerModel.js';
import { FixtureScreenshotView } from './FixtureScreenshotView.js';

type ComparisonStatus =
	| { readonly status: 'loading' }
	| { readonly status: 'ready'; readonly match: boolean }
	| { readonly status: 'error'; readonly message: string };

// ---------------------------------------------------------------------------
// Single fixture comparison (current vs baseline side-by-side)
// ---------------------------------------------------------------------------

class SingleComparisonModel extends ViewModel({
	fixtureId: prop.const<string>(),
	daemon: prop.const<DaemonModel>(),
	baselineSession: prop.const<string>(),
	currentSession: prop.const<string>(),
}) {
	readonly comparisonStatus = observableValue<ComparisonStatus>(this, { status: 'loading' });

	get fixtureId() { return this.props.fixtureId; }
	get daemon() { return this.props.daemon; }
	get baselineSession() { return this.props.baselineSession; }
	get currentSession() { return this.props.currentSession; }

	constructor(props: any) {
		super(props);
		this._store.add(autorun(reader => {
			const sourceTreeIds = this.props.daemon.sourceTreeIds.read(reader);
			const baselineStId = sourceTreeIds.get(this.props.baselineSession);
			const currentStId = sourceTreeIds.get(this.props.currentSession);
			if (baselineStId && currentStId) {
				this._fetchComparison();
			}
		}));
	}

	private async _fetchComparison(): Promise<void> {
		this.comparisonStatus.set({ status: 'loading' }, undefined);
		try {
			const result = await this.props.daemon.compare(
				this.props.fixtureId,
				this.props.baselineSession,
				this.props.currentSession,
			);
			this.comparisonStatus.set({ status: 'ready', match: result.match }, undefined);
		} catch (e) {
			this.comparisonStatus.set({ status: 'error', message: String(e) }, undefined);
		}
	}
}

const SingleComparison = viewWithModel(
	SingleComparisonModel,
	{},
	(reader, model) => {
		const status = model.comparisonStatus.read(reader);

		const statusBadge = status.status === 'loading'
			? <span style={compStyles.badgeLoading}>comparing...</span>
			: status.status === 'error'
				? <span style={compStyles.badgeError}>error</span>
				: status.match
					? <span style={compStyles.badgeMatch}>✓ match</span>
					: <span style={compStyles.badgeChanged}>● changed</span>;

		return (
			<div style={compStyles.fixtureRow}>
				<div style={compStyles.fixtureLabel}>
					<span style={compStyles.fixtureName}>{model.fixtureId}</span>
					{statusBadge}
				</div>
				<div style={compStyles.sideBySide}>
					<div style={compStyles.screenshotColumn}>
						<span style={compStyles.columnLabel}>Current</span>
						<FixtureScreenshotView
							fixtureId={model.fixtureId}
							sessionName={model.currentSession}
							daemon={model.daemon}
						/>
					</div>
					<div style={compStyles.screenshotColumn}>
						<span style={compStyles.columnLabel}>Baseline</span>
						<FixtureScreenshotView
							fixtureId={model.fixtureId}
							sessionName={model.baselineSession}
							daemon={model.daemon}
						/>
					</div>
				</div>
			</div>
		);
	}
);

// ---------------------------------------------------------------------------
// ScreenshotComparisonRow — renders a group of fixture comparisons
// ---------------------------------------------------------------------------

export const ScreenshotComparisonRow = view(
	{
		name: prop.const<string>(),
		path: prop.const<string>(),
		items: prop.const<readonly ScreenshotComparisonItem[]>(),
		daemon: prop.const<DaemonModel>(),
		baselineSession: prop.const<string>(),
		currentSession: prop.const<string>(),
	},
	(_reader, props) => {
		return (
			<div style={compStyles.groupContent}>
				{props.items.map((item) => (
					<SingleComparison
						key={item.fixtureId}
						fixtureId={item.fixtureId}
						daemon={props.daemon}
						baselineSession={props.baselineSession}
						currentSession={props.currentSession}
					/>
				))}
			</div>
		);
	}
);

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const compStyles = {
	groupHeader: {
		marginBottom: 12,
	} as React.CSSProperties,
	groupTitle: {
		fontSize: 13,
		fontWeight: 500,
		color: 'var(--component-explorer-foreground)',
	} as React.CSSProperties,
	groupContent: {
		display: 'flex',
		flexDirection: 'column',
		gap: 24,
	} as React.CSSProperties,
	fixtureRow: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
	} as React.CSSProperties,
	fixtureLabel: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	} as React.CSSProperties,
	fixtureName: {
		fontSize: 12,
		fontWeight: 500,
		color: 'var(--component-explorer-foreground)',
	} as React.CSSProperties,
	sideBySide: {
		display: 'flex',
		flexDirection: 'row',
		gap: 16,
	} as React.CSSProperties,
	screenshotColumn: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
		minWidth: 0,
	} as React.CSSProperties,
	columnLabel: {
		fontSize: 11,
		fontWeight: 500,
		color: 'var(--component-explorer-descriptionForeground)',
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
	} as React.CSSProperties,
	badgeMatch: {
		fontSize: 11,
		color: 'var(--component-explorer-testing-iconPassed)',
		fontWeight: 500,
	} as React.CSSProperties,
	badgeChanged: {
		fontSize: 11,
		color: 'var(--component-explorer-testing-iconFailed)',
		fontWeight: 500,
	} as React.CSSProperties,
	badgeLoading: {
		fontSize: 11,
		color: 'var(--component-explorer-descriptionForeground)',
		fontStyle: 'italic',
	} as React.CSSProperties,
	badgeError: {
		fontSize: 11,
		color: 'var(--component-explorer-errorForeground)',
		fontWeight: 500,
	} as React.CSSProperties,
};
